import React from 'react';
import { dashboardMuted } from '../../theme/dashboardLayout';
import { signalParticipationLines } from './reportingScanDisplay';
import { ScanTypeCoverageValue } from './scanCompleteness';

function barWidthPercent(value: number, scale: number): number {
  if (scale <= 0 || value <= 0) {
    return 0;
  }
  return Math.min(100, (value / scale) * 100);
}

function intensityLabel(uniqueUsers: number, totalEvents: number): string | null {
  if (uniqueUsers <= 0 || totalEvents <= 0) {
    return null;
  }
  const raw = totalEvents / uniqueUsers;
  const rounded = Math.round(raw * 10) / 10;
  const shown = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${shown} per person`;
}

type ChangeTone = 'up' | 'down' | 'flat';

function changeVsPrevious28(
  current: number,
  previous: number | null | undefined,
): { text: string; tone: ChangeTone } | null {
  if (previous == null) {
    return null;
  }

  const delta = current - previous;
  if (delta === 0) {
    return { text: 'no change', tone: 'flat' };
  }

  const sign = delta > 0 ? '+' : '';
  return { text: `${sign}${delta}`, tone: delta > 0 ? 'up' : 'down' };
}

function changeToneClass(tone: ChangeTone | undefined): string {
  if (tone === 'up') {
    return 'text-lime-green-dark';
  }
  if (tone === 'down') {
    return 'text-error-red';
  }
  return 'text-portal-teal/70';
}

function CountBar({
  label,
  value,
  scale,
  color,
  aside,
  asideTone,
}: {
  label: string;
  value: number;
  scale: number;
  color: string;
  aside?: string | null;
  asideTone?: ChangeTone;
}): React.ReactElement {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 text-[13px] leading-tight text-[#004b4d]">
        <span>{label}</span>
        {aside ? (
          <span className={`shrink-0 tabular-nums text-[13px] ${changeToneClass(asideTone)}`}>{aside}</span>
        ) : null}
      </div>
      <div className="mt-1.5">
        <div
          className="h-3.5 overflow-hidden rounded-full bg-[#e7edf8]"
          role="meter"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={scale}
          aria-valuenow={value}
        >
          <div
            className="h-full"
            style={{ width: `${barWidthPercent(value, scale)}%`, backgroundColor: color }}
          />
        </div>
      </div>
    </div>
  );
}

const ScanTypeCoveragePie = ({
  coverage,
  label,
}: {
  coverage: ScanTypeCoverageValue;
  label: string;
}): React.ReactElement => {
  const hasMembers = coverage.denominator > 0;
  const enrolledScale = Math.max(coverage.denominator, coverage.uniqueUsers, 0);
  const eventScale = Math.max(coverage.totalEvents, 0);
  const depth = signalParticipationLines(label, coverage.uniqueUsers, coverage.totalEvents).depth;
  const peopleChange = changeVsPrevious28(coverage.uniqueUsers, coverage.previousUniqueUsers);
  const signalChange = changeVsPrevious28(coverage.totalEvents, coverage.previousTotalEvents);
  const intensity = intensityLabel(coverage.uniqueUsers, coverage.totalEvents);

  return (
    <div className="relative mt-2 -mx-1 w-full min-w-0 sm:mx-0">
      {hasMembers ? (
        <div className="mt-2 space-y-4">
          <CountBar
            label={`${coverage.uniqueUsers} of ${coverage.denominator} enrolled`}
            value={coverage.uniqueUsers}
            scale={enrolledScale}
            color="#0d9488"
            aside={peopleChange?.text}
            asideTone={peopleChange?.tone}
          />
          <CountBar
            label={depth}
            value={coverage.totalEvents}
            scale={eventScale}
            color="#036939"
            aside={signalChange?.text ?? intensity}
            asideTone={signalChange?.tone}
          />
        </div>
      ) : (
        <p className={`mt-2 text-center ${dashboardMuted}`}>No enrolled people to chart</p>
      )}
    </div>
  );
};

export default ScanTypeCoveragePie;
